import { useState } from '#imports'
import { useApiMonOrga } from '~/composables/api/useApiMonOrga'
import {
  canUseExperimentalFeature,
  type ExperimentalFeaturesLoadStatus,
} from '~~/utils/experimental/experimentalFeatures'

type ExperimentalFeaturesState = {
  status: ExperimentalFeaturesLoadStatus
  keys: string[]
  requestSeq: number
}

type ExperimentalFeaturesMeResponse = {
  keys?: string[]
}

export function useExperimentalFeatures() {
  const state = useState<ExperimentalFeaturesState>('experimental-features', () => ({
    status: 'idle',
    keys: [],
    requestSeq: 0,
  }))

  function canUse(key: string): boolean {
    return canUseExperimentalFeature(state.value.status, state.value.keys, key)
  }

  function reset(): void {
    state.value = {
      status: 'idle',
      keys: [],
      requestSeq: state.value.requestSeq + 1,
    }
  }

  async function refresh(): Promise<void> {
    const requestSeq = state.value.requestSeq + 1
    state.value = {
      status: 'loading',
      keys: [],
      requestSeq,
    }
    try {
      const body = await useApiMonOrga<ExperimentalFeaturesMeResponse>(
        'GET',
        '/v1/experimental-features/me',
      )
      if (state.value.requestSeq !== requestSeq) {
        return
      }
      const keys = Array.isArray(body.keys) ? body.keys.filter((key) => typeof key === 'string') : []
      state.value = {
        status: 'ready',
        keys,
        requestSeq,
      }
    } catch {
      if (state.value.requestSeq !== requestSeq) {
        return
      }
      state.value = {
        status: 'error',
        keys: [],
        requestSeq,
      }
    }
  }

  return {
    state,
    canUse,
    refresh,
    reset,
  }
}
